import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  ColorPicker,
  Switch,
  Space,
  Tag,
  Popconfirm,
  message,
  Tabs,
  Row,
  Col,
  Divider,
  Typography,
  Alert,
  Drawer,
  InputNumber,
  Select,
  Upload,
  Tooltip,
  Slider,
  Radio,
  Checkbox,
  Image
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
  SettingOutlined,
  BgColorsOutlined as PaletteOutlined,
  MobileOutlined,
  DesktopOutlined,
  TabletOutlined,
  SaveOutlined,
  UndoOutlined,
  RedoOutlined,
  UploadOutlined,
  PictureOutlined,
  BorderOutlined,
  FontSizeOutlined,
  StarOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import ThemePresetSelector from '../../components/Theme/ThemePresetSelector';
import GradientTemplateSelector from '../../components/Theme/GradientTemplateSelector';
import ColorGradientSelector from '../../components/Theme/ColorGradientSelector';
import { ThemePreset } from '../../data/themePresets';
import { gradientTemplates, getGradientsByCategory, getAllGradientCategories, GradientTemplate } from '../../data/gradientTemplates';

const { Title, Text } = Typography;
const { TextArea } = Input;

// 主题编辑器组件
interface ThemeEditorProps {
  theme: any;
  visible: boolean;
  onClose: () => void;
  onSave: (theme: any) => void;
}

const ThemeEditor: React.FC<ThemeEditorProps> = ({ theme, visible, onClose, onSave }) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('colors');

  useEffect(() => {
    if (theme) {
      // 清理数据，确保所有颜色值都是字符串
      const cleanedTheme = {
        ...theme,
        variables: {
          ...theme.variables,
          // 确保所有颜色值都是字符串，只在undefined时使用默认值
          primaryColor: theme.variables?.primaryColor ?? '#1890ff',
          secondaryColor: theme.variables?.secondaryColor ?? '#722ed1',
          accentColor: theme.variables?.accentColor ?? '#13c2c2',
          successColor: theme.variables?.successColor ?? '#52c41a',
          warningColor: theme.variables?.warningColor ?? '#faad14',
          errorColor: theme.variables?.errorColor ?? '#ff4d4f',
          infoColor: theme.variables?.infoColor ?? '#1890ff',
          backgroundColor: theme.variables?.backgroundColor ?? '#f0f2f5',
          surfaceColor: theme.variables?.surfaceColor ?? '#ffffff',
          cardColor: theme.variables?.cardColor ?? '#ffffff',
          borderColor: theme.variables?.borderColor ?? '#d9d9d9',
          textColor: theme.variables?.textColor ?? '#262626',
          textSecondaryColor: theme.variables?.textSecondaryColor ?? '#8c8c8c',
          textDisabledColor: theme.variables?.textDisabledColor ?? '#bfbfbf',
          hoverColor: theme.variables?.hoverColor ?? '#f5f5f5',
          activeColor: theme.variables?.activeColor ?? '#e6f7ff',
          // 其他默认值
          backgroundOpacity: theme.variables?.backgroundOpacity ?? 1,
          cardOpacity: theme.variables?.cardOpacity ?? 1,
          borderOpacity: theme.variables?.borderOpacity ?? 1,
          backgroundImage: theme.variables?.backgroundImage ?? '',
          backgroundSize: theme.variables?.backgroundSize ?? 'cover',
          backgroundPosition: theme.variables?.backgroundPosition ?? 'center',
          backgroundRepeat: theme.variables?.backgroundRepeat ?? 'no-repeat',
          gradientPrimary: theme.variables?.gradientPrimary ?? '',
          gradientSecondary: theme.variables?.gradientSecondary ?? '',
          gradientBackground: theme.variables?.gradientBackground ?? '',
          gradientDirection: theme.variables?.gradientDirection ?? '135deg',
          borderRadius: theme.variables?.borderRadius ?? '8px',
          cardBorderRadius: theme.variables?.cardBorderRadius ?? '12px',
          buttonBorderRadius: theme.variables?.buttonBorderRadius ?? '6px',
          boxShadow: theme.variables?.boxShadow ?? '0 2px 8px rgba(0, 0, 0, 0.1)',
          cardShadow: theme.variables?.cardShadow ?? '0 4px 12px rgba(0, 0, 0, 0.15)',
          spacing: theme.variables?.spacing ?? '16px',
          padding: theme.variables?.padding ?? '16px',
          margin: theme.variables?.margin ?? '16px',
          transitionDuration: theme.variables?.transitionDuration ?? '0.3s',
          transitionTiming: theme.variables?.transitionTiming ?? 'ease-in-out',
          fontFamily: theme.variables?.fontFamily ?? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: theme.variables?.fontSize ?? '14px',
          fontWeight: theme.variables?.fontWeight ?? '400',
          lineHeight: theme.variables?.lineHeight ?? '1.5',
          mobileBreakpoint: theme.variables?.mobileBreakpoint ?? '768px',
          tabletBreakpoint: theme.variables?.tabletBreakpoint ?? '1024px',
          // 头部导航变量
          headerBackgroundColor: theme.variables?.headerBackgroundColor ?? '#ffffff',
          headerTextColor: theme.variables?.headerTextColor ?? '#262626',
          headerHeight: theme.variables?.headerHeight ?? '64px',
          headerPadding: theme.variables?.headerPadding ?? '0 24px',
          headerBoxShadow: theme.variables?.headerBoxShadow ?? '0 2px 8px rgba(0, 0, 0, 0.1)',
          headerBorderBottom: theme.variables?.headerBorderBottom ?? '1px solid #f0f0f0',
          headerOpacity: theme.variables?.headerOpacity ?? 1,
          // 导航条变量
          navBackgroundColor: theme.variables?.navBackgroundColor ?? '#ffffff',
          navTextColor: theme.variables?.navTextColor ?? '#262626',
          navHoverColor: theme.variables?.navHoverColor ?? '#f0f0f0',
          navActiveColor: theme.variables?.navActiveColor ?? '#e6f7ff',
          navHeight: theme.variables?.navHeight ?? '48px',
          navPadding: theme.variables?.navPadding ?? '0 16px',
          navBorderRadius: theme.variables?.navBorderRadius ?? '6px',
          navBoxShadow: theme.variables?.navBoxShadow ?? '0 1px 3px rgba(0, 0, 0, 0.1)',
          navOpacity: theme.variables?.navOpacity ?? 1,
          // Logo变量
          logoSize: theme.variables?.logoSize ?? '32px',
          logoColor: theme.variables?.logoColor ?? '#1890ff',
          logoHoverColor: theme.variables?.logoHoverColor ?? '#40a9ff',
          // 菜单项变量
          menuItemPadding: theme.variables?.menuItemPadding ?? '8px 16px',
          menuItemBorderRadius: theme.variables?.menuItemBorderRadius ?? '6px',
          menuItemHoverBackground: theme.variables?.menuItemHoverBackground ?? '#f0f0f0',
          menuItemActiveBackground: theme.variables?.menuItemActiveBackground ?? '#e6f7ff',
          menuItemTextColor: theme.variables?.menuItemTextColor ?? '#262626',
          menuItemHoverTextColor: theme.variables?.menuItemHoverTextColor ?? '#1890ff',
          menuItemActiveTextColor: theme.variables?.menuItemActiveTextColor ?? '#1890ff',
          customCSS: theme.variables?.customCSS ?? '',
          // 价格表专用变量
          pricingTableHeaderBg: theme.variables?.pricingTableHeaderBg ?? '',
          pricingTableHeaderHoverBg: theme.variables?.pricingTableHeaderHoverBg ?? '',
          pricingTableHeaderAccent: theme.variables?.pricingTableHeaderAccent ?? '',
          pricingTablePriceColor: theme.variables?.pricingTablePriceColor ?? '',
          pricingTableDiscountColor: theme.variables?.pricingTableDiscountColor ?? '',
          pricingTableDiscountBg: theme.variables?.pricingTableDiscountBg ?? '',
          pricingTableGameIconShadow: theme.variables?.pricingTableGameIconShadow ?? '',
          pricingTableFooterBg: theme.variables?.pricingTableFooterBg ?? ''
        }
      };
      form.setFieldsValue(cleanedTheme);
    }
  }, [theme, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      console.log('💾 表单数据验证通过:', values);
      console.log('🔍 关键变量检查:', {
        textColor: values.variables?.textColor,
        primaryColor: values.variables?.primaryColor,
        backgroundColor: values.variables?.backgroundColor,
        borderRadius: values.variables?.borderRadius,
        boxShadow: values.variables?.boxShadow,
        backgroundImage: values.variables?.backgroundImage
      });
      onSave(values);
      message.success('主题保存成功');
    } catch (error) {
      console.error('❌ 表单验证失败:', error);
      message.error('请检查表单填写');
    }
  };

  const colorItems = [
    { key: 'primaryColor', label: '主色调', description: '按钮、链接等主要元素颜色' },
    { key: 'secondaryColor', label: '辅助色', description: '次要元素颜色' },
    { key: 'accentColor', label: '强调色', description: '突出显示的颜色' },
    { key: 'successColor', label: '成功色', description: '成功状态颜色' },
    { key: 'warningColor', label: '警告色', description: '警告状态颜色' },
    { key: 'errorColor', label: '错误色', description: '错误状态颜色' },
    { key: 'infoColor', label: '信息色', description: '信息提示颜色' },
    { key: 'backgroundColor', label: '背景色', description: '页面背景颜色' },
    { key: 'surfaceColor', label: '表面色', description: '卡片等表面颜色' },
    { key: 'cardColor', label: '卡片色', description: '卡片背景颜色' },
    { key: 'borderColor', label: '边框色', description: '边框颜色' },
    { key: 'textColor', label: '文字色', description: '主要文字颜色' },
    { key: 'textSecondaryColor', label: '次要文字色', description: '次要文字颜色' },
    { key: 'textDisabledColor', label: '禁用文字色', description: '禁用状态文字颜色' },
    { key: 'hoverColor', label: '悬停色', description: '悬停状态颜色' },
    { key: 'activeColor', label: '激活色', description: '激活状态颜色' }
  ];

  const backgroundImageItems = [
    { key: 'backgroundImage', label: '背景图片', description: '页面背景图片URL' },
    { key: 'backgroundSize', label: '背景尺寸', description: '背景图片尺寸' },
    { key: 'backgroundPosition', label: '背景位置', description: '背景图片位置' },
    { key: 'backgroundRepeat', label: '背景重复', description: '背景图片重复方式' }
  ];

  const renderColorSection = () => (
    <div>
      <Title level={4}>颜色设置</Title>
      <Row gutter={[16, 16]}>
        {colorItems.map(item => (
          <Col xs={24} sm={12} md={8} lg={6} key={item.key}>
            <Form.Item
              name={['variables', item.key]}
              label={
                <div>
                  <div>{item.label}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {item.description}
                  </Text>
                </div>
              }
            >
              <ColorGradientSelector
                value={form.getFieldValue(['variables', item.key])}
                onChange={(value) => {
                  form.setFieldValue(['variables', item.key], value);
                }}
                showText
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderOpacitySection = () => (
    <div>
      <Title level={4}>透明度设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'backgroundOpacity']}
            label="背景透明度"
            tooltip="页面背景的透明度，0-1之间"
          >
            <Slider min={0} max={1} step={0.1} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'cardOpacity']}
            label="卡片透明度"
            tooltip="卡片的透明度，0-1之间"
          >
            <Slider min={0} max={1} step={0.1} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'borderOpacity']}
            label="边框透明度"
            tooltip="边框的透明度，0-1之间"
          >
            <Slider min={0} max={1} step={0.1} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderBackgroundSection = () => (
    <div>
      <Title level={4}>背景设置</Title>
      <Row gutter={[16, 16]}>
        {backgroundImageItems.map(item => (
          <Col xs={24} sm={12} md={6} key={item.key}>
            <Form.Item
              name={['variables', item.key]}
              label={item.label}
              tooltip={item.description}
            >
              {item.key === 'backgroundImage' ? (
                <Input placeholder="输入图片URL" />
              ) : (
                <Select>
                  {item.key === 'backgroundSize' && (
                    <>
                      <Select.Option value="cover">cover</Select.Option>
                      <Select.Option value="contain">contain</Select.Option>
                      <Select.Option value="100% 100%">100% 100%</Select.Option>
                      <Select.Option value="auto">auto</Select.Option>
                    </>
                  )}
                  {item.key === 'backgroundPosition' && (
                    <>
                      <Select.Option value="center">center</Select.Option>
                      <Select.Option value="top">top</Select.Option>
                      <Select.Option value="bottom">bottom</Select.Option>
                      <Select.Option value="left">left</Select.Option>
                      <Select.Option value="right">right</Select.Option>
                    </>
                  )}
                  {item.key === 'backgroundRepeat' && (
                    <>
                      <Select.Option value="no-repeat">no-repeat</Select.Option>
                      <Select.Option value="repeat">repeat</Select.Option>
                      <Select.Option value="repeat-x">repeat-x</Select.Option>
                      <Select.Option value="repeat-y">repeat-y</Select.Option>
                    </>
                  )}
                </Select>
              )}
            </Form.Item>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderGradientSection = () => (
    <div>
      <Title level={4}>渐变设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'gradientPrimary']}
            label="主渐变"
            tooltip="主要元素的渐变效果"
          >
            <Input placeholder="例如: linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'gradientSecondary']}
            label="辅助渐变"
            tooltip="辅助元素的渐变效果"
          >
            <Input placeholder="例如: linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name={['variables', 'gradientBackground']}
            label="背景渐变"
            tooltip="页面背景的渐变效果"
          >
            <Input placeholder="例如: linear-gradient(135deg, #667eea 0%, #764ba2 100%)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'gradientDirection']}
            label="渐变方向"
            tooltip="渐变的方向角度"
          >
            <Select>
              <Select.Option value="0deg">0° (水平)</Select.Option>
              <Select.Option value="45deg">45°</Select.Option>
              <Select.Option value="90deg">90° (垂直)</Select.Option>
              <Select.Option value="135deg">135°</Select.Option>
              <Select.Option value="180deg">180°</Select.Option>
              <Select.Option value="225deg">225°</Select.Option>
              <Select.Option value="270deg">270°</Select.Option>
              <Select.Option value="315deg">315°</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            label="渐变色模板"
            tooltip="选择预设的渐变色模板"
          >
            <GradientTemplateSelector
              onSelect={(template) => {
                form.setFieldsValue({
                  variables: {
                    gradientPrimary: template.gradient,
                    gradientDirection: template.direction
                  }
                });
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderBorderShadowSection = () => (
    <div>
      <Title level={4}>边框与阴影</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'borderRadius']}
            label="圆角半径"
            tooltip="通用圆角半径"
          >
            <Input placeholder="例如: 8px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'cardBorderRadius']}
            label="卡片圆角"
            tooltip="卡片的圆角半径"
          >
            <Input placeholder="例如: 12px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'buttonBorderRadius']}
            label="按钮圆角"
            tooltip="按钮的圆角半径"
          >
            <Input placeholder="例如: 6px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'boxShadow']}
            label="通用阴影"
            tooltip="通用元素的阴影效果"
          >
            <Input placeholder="例如: 0 2px 8px rgba(0, 0, 0, 0.1)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'cardShadow']}
            label="卡片阴影"
            tooltip="卡片的阴影效果"
          >
            <Input placeholder="例如: 0 4px 12px rgba(0, 0, 0, 0.15)" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderTypographySection = () => (
    <div>
      <Title level={4}>字体设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'fontFamily']}
            label="字体族"
            tooltip="页面使用的字体"
          >
            <Input placeholder="例如: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'fontSize']}
            label="字体大小"
            tooltip="基础字体大小"
          >
            <Input placeholder="例如: 14px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'fontWeight']}
            label="字体粗细"
            tooltip="字体粗细"
          >
            <Select>
              <Select.Option value="100">100 (Thin)</Select.Option>
              <Select.Option value="200">200 (Extra Light)</Select.Option>
              <Select.Option value="300">300 (Light)</Select.Option>
              <Select.Option value="400">400 (Normal)</Select.Option>
              <Select.Option value="500">500 (Medium)</Select.Option>
              <Select.Option value="600">600 (Semi Bold)</Select.Option>
              <Select.Option value="700">700 (Bold)</Select.Option>
              <Select.Option value="800">800 (Extra Bold)</Select.Option>
              <Select.Option value="900">900 (Black)</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'lineHeight']}
            label="行高"
            tooltip="文字行高"
          >
            <Input placeholder="例如: 1.5" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderSpacingSection = () => (
    <div>
      <Title level={4}>间距设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'spacing']}
            label="通用间距"
            tooltip="通用元素间距"
          >
            <Input placeholder="例如: 16px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'padding']}
            label="内边距"
            tooltip="元素内边距"
          >
            <Input placeholder="例如: 16px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'margin']}
            label="外边距"
            tooltip="元素外边距"
          >
            <Input placeholder="例如: 16px" />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderAnimationSection = () => (
    <div>
      <Title level={4}>动画设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'transitionDuration']}
            label="过渡时长"
            tooltip="动画过渡时长"
          >
            <Input placeholder="例如: 0.3s" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item
            name={['variables', 'transitionTiming']}
            label="过渡函数"
            tooltip="动画过渡函数"
          >
            <Select>
              <Select.Option value="ease">ease</Select.Option>
              <Select.Option value="ease-in">ease-in</Select.Option>
              <Select.Option value="ease-out">ease-out</Select.Option>
              <Select.Option value="ease-in-out">ease-in-out</Select.Option>
              <Select.Option value="linear">linear</Select.Option>
              <Select.Option value="cubic-bezier(0.4, 0, 0.2, 1)">cubic-bezier(0.4, 0, 0.2, 1)</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderHeaderNavSection = () => (
    <div>
      <Title level={4}>头部设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerBackgroundColor']}
            label="头部背景色"
            tooltip="网站头部的背景颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'headerBackgroundColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'headerBackgroundColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerTextColor']}
            label="头部文字颜色"
            tooltip="头部文字的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'headerTextColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'headerTextColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerHeight']}
            label="头部高度"
            tooltip="头部的高度，如: 64px"
          >
            <Input placeholder="例如: 64px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerPadding']}
            label="头部内边距"
            tooltip="头部的内边距，如: 0 24px"
          >
            <Input placeholder="例如: 0 24px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerBoxShadow']}
            label="头部阴影"
            tooltip="头部的阴影效果"
          >
            <Input placeholder="例如: 0 2px 8px rgba(0, 0, 0, 0.1)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerBorderBottom']}
            label="头部底边框"
            tooltip="头部的底边框样式"
          >
            <Input placeholder="例如: 1px solid #f0f0f0" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'headerOpacity']}
            label="头部透明度"
            tooltip="头部的透明度 (0-1)"
          >
            <Slider min={0} max={1} step={0.1} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>导航条设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navBackgroundColor']}
            label="导航条背景色"
            tooltip="导航条的背景颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'navBackgroundColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'navBackgroundColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navTextColor']}
            label="导航条文字颜色"
            tooltip="导航条文字的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'navTextColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'navTextColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navHoverColor']}
            label="导航条悬停颜色"
            tooltip="导航条悬停时的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'navHoverColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'navHoverColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navActiveColor']}
            label="导航条激活颜色"
            tooltip="导航条激活时的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'navActiveColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'navActiveColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navHeight']}
            label="导航条高度"
            tooltip="导航条的高度，如: 48px"
          >
            <Input placeholder="例如: 48px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navPadding']}
            label="导航条内边距"
            tooltip="导航条的内边距，如: 0 16px"
          >
            <Input placeholder="例如: 0 16px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navBorderRadius']}
            label="导航条圆角"
            tooltip="导航条的圆角大小"
          >
            <Input placeholder="例如: 6px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navBoxShadow']}
            label="导航条阴影"
            tooltip="导航条的阴影效果"
          >
            <Input placeholder="例如: 0 1px 3px rgba(0, 0, 0, 0.1)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'navOpacity']}
            label="导航条透明度"
            tooltip="导航条的透明度 (0-1)"
          >
            <Slider min={0} max={1} step={0.1} />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>Logo设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'logoSize']}
            label="Logo大小"
            tooltip="Logo的字体大小，如: 32px"
          >
            <Input placeholder="例如: 32px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'logoColor']}
            label="Logo颜色"
            tooltip="Logo的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'logoColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'logoColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'logoHoverColor']}
            label="Logo悬停颜色"
            tooltip="Logo悬停时的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'logoHoverColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'logoHoverColor'], value);
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Title level={4}>菜单项设置</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemPadding']}
            label="菜单项内边距"
            tooltip="菜单项的内边距，如: 8px 16px"
          >
            <Input placeholder="例如: 8px 16px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemBorderRadius']}
            label="菜单项圆角"
            tooltip="菜单项的圆角大小"
          >
            <Input placeholder="例如: 6px" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemHoverBackground']}
            label="菜单项悬停背景"
            tooltip="菜单项悬停时的背景色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'menuItemHoverBackground'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'menuItemHoverBackground'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemActiveBackground']}
            label="菜单项激活背景"
            tooltip="菜单项激活时的背景色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'menuItemActiveBackground'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'menuItemActiveBackground'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemTextColor']}
            label="菜单项文字颜色"
            tooltip="菜单项文字的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'menuItemTextColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'menuItemTextColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemHoverTextColor']}
            label="菜单项悬停文字颜色"
            tooltip="菜单项悬停时文字的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'menuItemHoverTextColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'menuItemHoverTextColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'menuItemActiveTextColor']}
            label="菜单项激活文字颜色"
            tooltip="菜单项激活时文字的颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'menuItemActiveTextColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'menuItemActiveTextColor'], value);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderPricingTableSection = () => (
    <div>
      <Title level={4}>价格表样式</Title>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'pricingTableHeaderBg']}
            label="表头背景渐变"
            tooltip="价格表表头的背景渐变效果"
          >
            <Input placeholder="例如: linear-gradient(135deg, #722ed1 0%, #1890ff 100%)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'pricingTableHeaderHoverBg']}
            label="表头悬停背景"
            tooltip="价格表表头悬停时的背景渐变"
          >
            <Input placeholder="例如: linear-gradient(135deg, #1890ff 0%, #722ed1 100%)" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name={['variables', 'pricingTableHeaderAccent']}
            label="表头装饰条渐变"
            tooltip="价格表表头装饰条的渐变效果"
          >
            <Input placeholder="例如: linear-gradient(90deg, #13c2c2, #1890ff, #722ed1)" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'pricingTablePriceColor']}
            label="价格文字颜色"
            tooltip="价格显示的文字颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'pricingTablePriceColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'pricingTablePriceColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'pricingTableDiscountColor']}
            label="折扣文字颜色"
            tooltip="折扣显示的文字颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'pricingTableDiscountColor'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'pricingTableDiscountColor'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'pricingTableDiscountBg']}
            label="折扣背景色"
            tooltip="折扣标签的背景颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'pricingTableDiscountBg'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'pricingTableDiscountBg'], value);
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name={['variables', 'pricingTableGameIconShadow']}
            label="游戏图标阴影"
            tooltip="游戏图标的阴影效果"
          >
            <Input placeholder="例如: 0 2px 8px rgba(0, 0, 0, 0.1)" />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name={['variables', 'pricingTableFooterBg']}
            label="页脚背景色"
            tooltip="价格表页脚的背景颜色"
          >
            <ColorGradientSelector
              value={form.getFieldValue(['variables', 'pricingTableFooterBg'])}
              onChange={(value) => {
                form.setFieldValue(['variables', 'pricingTableFooterBg'], value);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderCustomCSSSection = () => (
    <div>
      <Title level={4}>自定义CSS</Title>
      <Form.Item
        name={['variables', 'customCSS']}
        label="自定义样式"
        tooltip="添加自定义CSS代码"
      >
        <TextArea
          rows={8}
          placeholder="例如:&#10;.custom-class {&#10;  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);&#10;  border-radius: 20px;&#10;}"
        />
      </Form.Item>
    </div>
  );

  const tabItems = [
    {
      key: 'colors',
      label: '颜色',
      icon: <PaletteOutlined />,
      children: renderColorSection()
    },
    {
      key: 'opacity',
      label: '透明度',
      icon: <PictureOutlined />,
      children: renderOpacitySection()
    },
    {
      key: 'background',
      label: '背景',
      icon: <PictureOutlined />,
      children: renderBackgroundSection()
    },
    {
      key: 'gradients',
      label: '渐变',
      icon: <PictureOutlined />,
      children: renderGradientSection()
    },
    {
      key: 'borders',
      label: '边框与阴影',
      icon: <BorderOutlined />,
      children: renderBorderShadowSection()
    },
    {
      key: 'typography',
      label: '字体',
      icon: <FontSizeOutlined />,
      children: renderTypographySection()
    },
    {
      key: 'spacing',
      label: '间距',
      icon: <BorderOutlined />,
      children: renderSpacingSection()
    },
    {
      key: 'animation',
      label: '动画',
      icon: <BorderOutlined />,
      children: renderAnimationSection()
    },
    {
      key: 'header',
      label: '头部导航',
      icon: <DesktopOutlined />,
      children: renderHeaderNavSection()
    },
    {
      key: 'pricing',
      label: '价格表',
      icon: <DollarOutlined />,
      children: renderPricingTableSection()
    },
    {
      key: 'custom',
      label: '自定义CSS',
      icon: <SettingOutlined />,
      children: renderCustomCSSSection()
    }
  ];

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EditOutlined />
          主题编辑器
          {theme?.id && <Tag color="blue">编辑模式</Tag>}
        </div>
      }
      open={visible}
      onClose={onClose}
      width="80%"
      style={{ top: 20 }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Space>
            <Button icon={<UndoOutlined />}>撤销</Button>
            <Button icon={<RedoOutlined />}>重做</Button>
          </Space>
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
              保存
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />
      </Form>
    </Drawer>
  );
};

// 主组件
const ModernThemeManagement: React.FC = () => {
  const { themes, loading, createTheme, updateTheme, deleteTheme, setTheme, setDefaultTheme } = useTheme();
  const { user } = useAuth();
  const [editingTheme, setEditingTheme] = useState<any>(null);
  const [editorVisible, setEditorVisible] = useState(false);
  const [presetSelectorVisible, setPresetSelectorVisible] = useState(false);
  const [previewTheme, setPreviewTheme] = useState<ThemePreset | null>(null);

  if (!user || user.role !== 'admin') {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Alert
          message="权限不足"
          description="您没有权限访问主题管理页面"
          type="warning"
          showIcon
        />
      </div>
    );
  }

  const handleCreate = () => {
    setPresetSelectorVisible(true);
  };

  const handleCreateFromScratch = () => {
    setEditingTheme({
      name: '',
      isDefault: false,
      variables: {
        primaryColor: '#1890ff',
        secondaryColor: '#722ed1',
        accentColor: '#13c2c2',
        successColor: '#52c41a',
        warningColor: '#faad14',
        errorColor: '#ff4d4f',
        infoColor: '#1890ff',
        backgroundColor: '#f0f2f5',
        surfaceColor: '#ffffff',
        cardColor: '#ffffff',
        borderColor: '#d9d9d9',
        textColor: '#262626',
        textSecondaryColor: '#8c8c8c',
        textDisabledColor: '#bfbfbf',
        hoverColor: '#f5f5f5',
        activeColor: '#e6f7ff',
        backgroundOpacity: 1,
        cardOpacity: 1,
        borderOpacity: 1,
        backgroundImage: '',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        gradientPrimary: '',
        gradientSecondary: '',
        gradientBackground: '',
        gradientDirection: '135deg',
        borderRadius: '8px',
        cardBorderRadius: '12px',
        buttonBorderRadius: '6px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        cardShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        spacing: '16px',
        padding: '16px',
        margin: '16px',
        transitionDuration: '0.3s',
        transitionTiming: 'ease-in-out',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: '1.5',
        mobileBreakpoint: '768px',
        tabletBreakpoint: '1024px',
        customCSS: '',
        // 价格表专用变量默认值
        pricingTableHeaderBg: '',
        pricingTableHeaderHoverBg: '',
        pricingTableHeaderAccent: '',
        pricingTablePriceColor: '',
        pricingTableDiscountColor: '',
        pricingTableDiscountBg: '',
        pricingTableGameIconShadow: '',
        pricingTableFooterBg: ''
      }
    });
    setEditorVisible(true);
  };

  const handlePresetSelect = (preset: ThemePreset) => {
    setEditingTheme({
      name: `${preset.name} - 自定义`,
      isDefault: false,
      variables: { ...preset.variables }
    });
    setEditorVisible(true);
  };

  const handlePresetPreview = (preset: ThemePreset) => {
    setPreviewTheme(preset);
    // 这里可以添加预览逻辑
    message.info(`预览主题: ${preset.name}`);
  };

  const handleEdit = (theme: any) => {
    setEditingTheme(theme);
    setEditorVisible(true);
  };

  const handleSave = async (themeData: any) => {
    try {
      if (editingTheme?.id) {
        await updateTheme(editingTheme.id, themeData);
        message.success('主题更新成功');
      } else {
        await createTheme(themeData);
        message.success('主题创建成功');
      }
      setEditorVisible(false);
      setEditingTheme(null);
    } catch (error) {
      message.error('保存失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTheme(id);
      message.success('主题删除成功');
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSetActive = async (id: number) => {
    try {
      await setTheme(id);
      message.success('主题已激活');
    } catch (error) {
      message.error('激活失败');
    }
  };

  const handleSetDefault = async (id: number) => {
    try {
      await setDefaultTheme(id);
      message.success('已设为默认主题');
    } catch (error) {
      message.error('设置失败');
    }
  };

  const columns = [
    {
      title: '主题名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: any) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.isActive && <Tag color="green">已激活</Tag>}
            {record.isDefault && <Tag color="blue">默认</Tag>}
          </div>
        </div>
      )
    },
    {
      title: '创建者',
      dataIndex: 'createdByName',
      key: 'createdByName'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text: string) => new Date(text).toLocaleDateString()
    },
    {
      title: '操作',
      key: 'actions',
      render: (text: string, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={() => handleEdit({ ...record, id: undefined, name: `${record.name} - 副本` })}
          >
            复制
          </Button>
          {!record.isActive && (
            <Button
              type="primary"
              size="small"
              onClick={() => handleSetActive(record.id)}
            >
              激活
            </Button>
          )}
          {!record.isDefault && (
            <Button
              size="small"
              onClick={() => handleSetDefault(record.id)}
            >
              设默认
            </Button>
          )}
          <Popconfirm
            title="确定要删除这个主题吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="modern-theme-management-page">
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PaletteOutlined />
            现代化主题管理
            <Tag color="purple">NEW</Tag>
          </div>
        }
        extra={
          <Space>
            <Button
              icon={<PlusOutlined />}
              onClick={handleCreateFromScratch}
            >
              从空白创建
            </Button>
            <Button
              type="primary"
              icon={<StarOutlined />}
              onClick={handleCreate}
            >
              从预设创建
            </Button>
          </Space>
        }
      >
        <Alert
          message="🎨 全新主题系统"
          description="现在支持透明度、背景图片、渐变效果、自定义圆角、阴影、字体设置等更多功能！"
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />
        
        <Table
          columns={columns}
          dataSource={themes}
          rowKey="id"
          loading={loading}
          className="admin-theme-list-table"
          pagination={false}
        />
      </Card>

      <ThemeEditor
        theme={editingTheme}
        visible={editorVisible}
        onClose={() => setEditorVisible(false)}
        onSave={handleSave}
      />

      <ThemePresetSelector
        visible={presetSelectorVisible}
        onClose={() => setPresetSelectorVisible(false)}
        onSelect={handlePresetSelect}
        onPreview={handlePresetPreview}
      />
    </div>
  );
};

export default ModernThemeManagement;